import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { PagesEnum } from "../../../utils/enums";
import { parseJwt } from "../../../utils/help/user/user";
import { RootState } from "../../redux/app/store";
import {
  selectCustomerLoyaltyOffersRaw,
  selectLoyaltyCart,
} from "../../redux/selectors/loyaltyCartSelectors";
import { selectIsPayWithPointsApplied } from "../../redux/selectors/payWithPointsSelectors";
import { useAppDispatch, useAppSelector } from "../useReduxHooks";
import { useLoyaltyContext } from "./useLoyaltyContext";

const AUTO_APPLY_PATHNAMES = [
  PagesEnum.Home,
  PagesEnum.Cart,
  PagesEnum.Rewards,
  PagesEnum.AllRewards,
].map((page) => `/${page}`);

const selectHasCompetingReward = (state: RootState): boolean => {
  const loyaltyCart = selectLoyaltyCart(state);

  return (
    !!loyaltyCart.redemption ||
    !!loyaltyCart.payWithPoints ||
    !!loyaltyCart.applyFlow?.redirect ||
    !!loyaltyCart.applyFlow?.selectedOfferId ||
    !!loyaltyCart.offerBuilder ||
    selectIsPayWithPointsApplied(state)
  );
};

// ----------------------The loyalty service decides which offer (at most one) carries autoApply; this only applies it-----------------------
export function useAutoApplyOffer() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const router = useRouter();
  const {
    useLoyaltyService,
    timeZone,
    mappedOrderType,
    channel,
    selectedFranchiseId,
    restaurantId,
  } = useLoyaltyContext();

  // ----------------------Stable or primitive picks only: this lives in BackgroundHooks, so any changing reference re-renders every app-wide hook-----------------------
  const customerOffers = useAppSelector(selectCustomerLoyaltyOffersRaw);
  const hasCompetingReward = useAppSelector(selectHasCompetingReward);
  const isOrderDataLoading = useAppSelector(
    (state) => state.globalComponentsToggleSlice.orderDataLoading
  );
  const token = useAppSelector((state) => state.userInfo.token);

  // ----------------------Offers are persisted, so only a list fetched after mount is trusted to still be current-----------------------
  const offersAtMount = useRef(customerOffers);
  const isApplying = useRef(false);

  useEffect(() => {
    const customerId = parseJwt(token)?.customer_id;

    if (
      !useLoyaltyService ||
      isOrderDataLoading ||
      hasCompetingReward ||
      customerOffers === offersAtMount.current ||
      !customerOffers?.some((offer) => offer.autoApply) ||
      !selectedFranchiseId ||
      !mappedOrderType ||
      !AUTO_APPLY_PATHNAMES.includes(router.pathname) ||
      !customerId ||
      isApplying.current
    ) {
      return;
    }

    // ----------------------Loaded on demand so the apply flow stays out of the bundle every page ships-----------------------
    isApplying.current = true;
    Promise.all([
      import("../../../utils/loyalty/autoApplyOffer"),
      import("../../redux/thunks/applyLoyaltyItemOfferFlow"),
    ])
      .then(([autoApplyOffer, applyFlow]) => {
        // ----------------------Read fresh: the offers may have changed while the code was loading-----------------------
        const state = store.getState();
        const offer = autoApplyOffer.findAutoApplyOffer(
          selectCustomerLoyaltyOffersRaw(state),
          {
            timeZone,
            orderType: mappedOrderType,
            channel,
          }
        );
        if (!offer || selectHasCompetingReward(state)) {
          return;
        }

        // ----------------------Once per customer and offer, so a guest who removes it keeps it removed-----------------------
        const autoApplyKey = `${restaurantId}:${customerId}:${offer.id}`;
        if (autoApplyOffer.wasOfferAutoApplied(autoApplyKey)) {
          return;
        }
        autoApplyOffer.markOfferAutoApplied(autoApplyKey);

        applyFlow.dispatchApplyLoyaltyItemOfferFlow(dispatch, store.getState, {
          offer,
          menuSubItems: [],
        });
      })
      .catch((error) => console.error("Offer auto-apply failed:", error))
      .finally(() => {
        isApplying.current = false;
      });
  }, [
    useLoyaltyService,
    isOrderDataLoading,
    hasCompetingReward,
    customerOffers,
    selectedFranchiseId,
    mappedOrderType,
    router.pathname,
    token,
    timeZone,
    channel,
    restaurantId,
    dispatch,
    store,
  ]);
}
